import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import {QueryWarehouseToolCall} from 'llm/deep-research/tool-details/types';
import LLMMarkdownSection from 'llm/markdown/LLMMarkdownSection';
import MonacoEditor from 'llm/monaco/MonacoEditor';
import {FONT_PROXIMA_14, FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import {GRAY_DARKER} from 'ui-library/Colors';

export interface QueryWarehouseToolDetailProps {
  toolCall: QueryWarehouseToolCall;
}

const MIN_EDITOR_HEIGHT = 120; // Roughly 5 rows of text
const MAX_EDITOR_HEIGHT = 320;
const BORDER_OFFSET = 2;

/**
 * Component for displaying detailed information about QueryWarehouse tool calls.
 * Shows the SQL query input with syntax highlighting and markdown-rendered results.
 */
export function QueryWarehouseToolDetail({toolCall}: QueryWarehouseToolDetailProps): JSX.Element {
  const [queryEditorHeight, setQueryEditorHeight] = useState(MIN_EDITOR_HEIGHT);

  return (
    <div className={css(styles.container)}>
      <div className={css(styles.section)}>
        <div className={css(styles.sectionLabel)}>Query:</div>
        <div className={css(styles.editorContainer)}>
          <MonacoEditor
            value={toolCall.query || ''}
            language="pgsql"
            height={queryEditorHeight}
            width="100%"
            theme="vs"
            options={{
              readOnly: true,
              minimap: {enabled: false},
              scrollBeyondLastLine: false,
              wordWrap: 'on',
            }}
            onChangeContentSize={(e) => {
              setQueryEditorHeight(
                Math.min(Math.max(MIN_EDITOR_HEIGHT, e.contentHeight + BORDER_OFFSET), MAX_EDITOR_HEIGHT)
              );
            }}
          />
        </div>
      </div>

      <div className={css(styles.section)}>
        <div className={css(styles.sectionLabel)}>Results:</div>
        <div className={css(styles.markdownContainer)}>
          <LLMMarkdownSection markdown={toolCall.responseText?.trim() || 'No response available'} />
        </div>
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },

  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },

  sectionLabel: {
    ...FONT_PROXIMA_14,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    color: GRAY_DARKER,
  },

  editorContainer: {
    border: '1px solid #e1e5e9',
    borderRadius: '4px',
    overflow: 'hidden',
  },

  markdownContainer: {
    ...FONT_PROXIMA_14,
    padding: '0 12px',
    backgroundColor: '#fcfdfe',
    border: '1px solid #e1e5e9',
    borderRadius: '4px',
  },
});
